import { Grid2 as Grid } from '@mui/material';
import AdminMetricCard from '../shared/AdminMetricCard';
import PeopleIcon from '@mui/icons-material/People';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import { AdminDashboardMetrics } from '../../../types/admin';

interface MetricsOverviewProps {
  metrics: AdminDashboardMetrics | null;
  loading?: boolean;
}

/**
 * MetricsOverview - Grid of key platform metrics
 *
 * Displays 4 primary metrics:
 * - Total Users (with month-over-month change)
 * - Active Homes (with month-over-month change)
 * - Monthly Recurring Revenue (with month-over-month change)
 * - Active Businesses (with month-over-month change)
 *
 * Features:
 * - Responsive grid layout (4 columns on desktop, 2 on tablet, 1 on mobile)
 * - Trend indicators (up/down arrows with percentages)
 * - Color-coded icons
 * - Loading state support
 */
export default function MetricsOverview({ metrics, loading }: MetricsOverviewProps) {
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        <AdminMetricCard
          title="Total Users"
          value={metrics ? formatNumber(metrics.totalUsers) : '0'}
          icon={<PeopleIcon />}
          color="#1976d2"
          trend={
            metrics
              ? {
                  value: metrics.totalUsersChange,
                  label: 'vs last month',
                  direction: metrics.totalUsersChange >= 0 ? 'up' : 'down',
                }
              : undefined
          }
          loading={loading}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        <AdminMetricCard
          title="Active Homes"
          value={metrics ? formatNumber(metrics.activeHomes) : '0'}
          icon={<HomeIcon />}
          color="#2e7d32"
          trend={
            metrics
              ? {
                  value: metrics.activeHomesChange,
                  label: 'vs last month',
                  direction: metrics.activeHomesChange >= 0 ? 'up' : 'down',
                }
              : undefined
          }
          loading={loading}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        <AdminMetricCard
          title="Monthly Recurring Revenue"
          value={metrics ? formatCurrency(metrics.mrr) : '$0'}
          subtitle="MRR"
          icon={<AttachMoneyIcon />}
          color="#ed6c02"
          trend={
            metrics
              ? {
                  value: metrics.mrrChange,
                  label: 'vs last month',
                  direction: metrics.mrrChange >= 0 ? 'up' : 'down',
                }
              : undefined
          }
          loading={loading}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        <AdminMetricCard
          title="Active Businesses"
          value={metrics ? formatNumber(metrics.activeBusinesses) : '0'}
          icon={<BusinessIcon />}
          color="#9c27b0"
          trend={
            metrics
              ? {
                  value: metrics.activeBusinessesChange,
                  label: 'vs last month',
                  direction: metrics.activeBusinessesChange >= 0 ? 'up' : 'down',
                }
              : undefined
          }
          loading={loading}
        />
      </Grid>
    </Grid>
  );
}
